import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';
import './Home.css';

export default function Home() {
  const featured = products.filter(p => p.featured);
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translateY(${y * 0.35}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <main className="home page-enter">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" ref={heroRef}>
          <img
            src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1600&q=80"
            alt="Alainn Collection"
          />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <p className="hero-eyebrow">Spring — Summer 2025</p>
          <h1 className="hero-title">
            <em>Dress for the</em><br />
            life you desire
          </h1>
          <p className="hero-sub">Curated pieces for the woman who moves through the world with quiet intention.</p>
          <div className="hero-actions">
            <Link to="/shop" className="hero-btn primary-btn">Explore Collection</Link>
            <Link to="/shop?cat=New" className="hero-btn ghost-btn">New Arrivals</Link>
          </div>
        </div>
        <div className="hero-scroll-hint">
          <span>Scroll</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* Brand strip */}
      <section className="brand-strip">
        <div className="strip-inner">
          <div className="strip-item">
            <span className="strip-num">01</span>
            <span className="strip-text">Considered Design</span>
          </div>
          <div className="strip-divider" />
          <div className="strip-item">
            <span className="strip-num">02</span>
            <span className="strip-text">Artisan Craft</span>
          </div>
          <div className="strip-divider" />
          <div className="strip-item">
            <span className="strip-num">03</span>
            <span className="strip-text">Timeless Pieces</span>
          </div>
          <div className="strip-divider" />
          <div className="strip-item">
            <span className="strip-num">04</span>
            <span className="strip-text">Complimentary Shipping €250+</span>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="featured-section">
        <div className="section-header">
          <div className="section-header-line" />
          <div className="section-header-text">
            <p className="section-eyebrow">The Edit</p>
            <h2 className="section-title">Featured Pieces</h2>
          </div>
        </div>
        <div className="featured-grid">
          {featured.map(p => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
        <div className="section-cta">
          <Link to="/shop" className="underline-btn">View All Pieces →</Link>
        </div>
      </section>

      {/* Editorial banner */}
      <section className="editorial-banner">
        <div className="editorial-img">
          <img src="https://images.unsplash.com/photo-1583744946564-b52ac1c389c8?w=1200&q=80" alt="Editorial" />
        </div>
        <div className="editorial-text">
          <p className="editorial-eyebrow">The Philosophy</p>
          <h2 className="editorial-title">Buy less.<br /><em>Choose well.</em><br />Make it last.</h2>
          <p className="editorial-body">
            Alainn was founded on a simple belief: that clothing should be bought once, cherished always. We source exclusively from independent ateliers who share our commitment to slowness, beauty, and the kind of quality that only improves with time.
          </p>
          <Link to="/shop" className="editorial-btn">Discover the Collection</Link>
        </div>
      </section>

      {/* New arrivals strip */}
      <section className="new-arrivals-section">
        <div className="section-header centered">
          <p className="section-eyebrow">Just In</p>
          <h2 className="section-title">New Arrivals</h2>
        </div>
        <div className="new-grid">
          {products.filter(p => p.badge === 'New Arrival').slice(0, 4).map(p => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Category tiles */}
      <section className="category-tiles-section">
        <div className="section-header centered">
          <p className="section-eyebrow">Browse by Category</p>
          <h2 className="section-title">Shop the Collection</h2>
        </div>
        <div className="category-tiles">
          {[
            { label: "Dresses", img: "https://images.unsplash.com/photo-1572804013427-4d7ca7268217?w=600&q=80", cat: "Dresses" },
            { label: "Tops", img: "https://images.unsplash.com/photo-1564584217132-2271feaeb3c5?w=600&q=80", cat: "Tops" },
            { label: "Skirts", img: "https://images.unsplash.com/photo-1583496661160-fb5886a0aaaa?w=600&q=80", cat: "Skirts" },
            { label: "Knitwear", img: "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&q=80", cat: "Knitwear" },
            { label: "Outerwear", img: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?w=600&q=80", cat: "Outerwear" },
            { label: "Shoes", img: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=600&q=80", cat: "Shoes" },
            { label: "Bags", img: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&q=80", cat: "Bags" },
            { label: "Jewellery", img: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=600&q=80", cat: "Jewellery" },
          ].map(({ label, img, cat }) => (
            <Link to={`/shop?cat=${cat}`} className="category-tile" key={cat}>
              <div className="tile-img">
                <img src={img} alt={label} />
                <div className="tile-overlay" />
              </div>
              <span className="tile-label">{label}</span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
